import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import "./login.css";
import { useNavigate } from "react-router";
import { getReturnUrl } from "../../utils/auth";

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);
  const emailRef = useRef(null);

  // Use absolute WordPress REST API path
  const API_ENDPOINT = "/wp-json/dbsb/v1/login";

  // // My API endpoint directory
  // const API_ENDPOINT = "../../../../includes/api/auth-api.php/wp-json/dbsb/v1/login";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    switch (name) {
      case "email":
        newErrors.email = !value
          ? "Email is required"
          : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? "Must be a valid email address"
          : "";
        break;
      case "password":
        newErrors.password = !value ? "Password is required" : "";
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromOtp = urlParams.get("fromOtp"); // New param to indicate return from OTP

    if (fromOtp === "true") {
      setTimeout(() => {
        if (emailRef.current) {
          emailRef.current.focus();
          emailRef.current.select();
        }
      }, 100);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = {
      email: true,
      password: true,
    };
    Object.keys(formData).forEach((field) =>
      validateField(field, formData[field])
    );

    if (
      Object.values(formData).some((value) => !value) ||
      Object.values(errors).some((error) => error)
    ) {
      return;
    }

    setIsLoading(true);
    setApiError(null);
    setApiSuccess(null);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      setApiSuccess("Login successful!");
      setFormData({
        email: "",
        password: "",
      });
      
      // Check for return URL in query params
      const returnUrl = getReturnUrl(window.location.search);
      
      // Redirect to return URL or dashboard
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        window.location.href = "/dashboard";
      }
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <motion.div
      className="login-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="login-card" variants={containerVariants}>
        <div className="logo">
          <a href="/">
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
              alt="Direct by Syndicate Bio"
            />
          </a>
        </div>
        <motion.div
          className="login-content"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>Welcome, it’s good to see you</h2>
          <p>Log in to your account</p>
          <motion.form
            onSubmit={handleSubmit}
            className="login-form"
            variants={formVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                ref={emailRef}
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </motion.div>
            <motion.div
              variants={itemVariants}
              className="form-group password-group"
            >
              <label>Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                />
                <span
                  className={`toggle-password ${
                    formData.password ? "visible" : "hidden"
                  }`}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </span>
              </div>
              {errors.password && (
                <span className="error">{errors.password}</span>
              )}
            </motion.div>
            {apiError && (
              <motion.p className="error-message" variants={itemVariants}>
                {apiError}
              </motion.p>
            )}
            <div className="forgot-password-box">
              <a
                className="forgot-password-link"
                href="/forgot-password"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/forgot-password");
                }}
              >
                Forgot Password
              </a>
            </div>
            {/* {apiSuccess && (
              <motion.p className="success-message" variants={itemVariants}>
                {apiSuccess}
              </motion.p>
            )} */}
            <motion.div variants={itemVariants}>
              <motion.button
                type="submit"
                className={`login-button ${
                  isLoading ||
                  Object.values(formData).some((value) => !value) ||
                  Object.values(errors).some((error) => error)
                    ? "disabled"
                    : ""
                }`}
                disabled={
                  isLoading ||
                  Object.values(formData).some((value) => !value) ||
                  Object.values(errors).some((error) => error)
                }
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {isLoading ? "Logging in..." : "Log in with email"}
              </motion.button>
            </motion.div>
          </motion.form>
          <div className="login-section">
            <div className="or-line">
              <ul></ul>
              <p className="login-link">OR</p>
              <ul></ul>
            </div>
            <motion.a
              href="/register" // Redirect to signup -- you can change this as needed
              onClick={(e) => {
                e.preventDefault();
                navigate("/register");
              }}
              className="signup-link"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.4 }}
            >
              Don’t have an account? Sign up
            </motion.a>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Login;
